/**
 * product.js
 *
 * Shared behavior for all three PDP templates (digital/yarn/accessory).
 * Product forms keep their real native Shopify POST fallback. When the
 * global MvcCart controller is available, the same forms are progressively
 * enhanced to /cart/add.js, refreshed from Shopify server state, and open
 * the existing global Cart Drawer. The Digital bundle builder keeps its
 * existing selection architecture and delegates its one multi-item add to
 * that same Cart controller.
 *
 * VARIANT MATCHING: a visually-hidden native `<select data-variant-
 * select>` carries one real `<option>` per `product.variants`, each
 * with `data-option1/2/3` (the variant's real option values) and
 * `data-price`/`data-compare-at-price`/`data-available`/etc as plain
 * data (all server-rendered by Liquid — nothing here fabricates a
 * price or availability). Visible swatch/pill buttons carry
 * `data-option-position`/`data-option-value`; clicking one updates an
 * in-memory `{position: value}` state, finds the `<option>` whose
 * data-option1/2/3 matches that full state, and applies it. This is
 * the standard real-Shopify variant-matching technique (not tied to
 * any specific theme's design) — necessary integration logic, not a
 * copied UI.
 *
 * MONEY FORMATTING: quantity-multiplied totals (yarn/accessory only —
 * digital has no quantity control) need to be formatted in the shop's
 * real currency format, which this theme cannot safely guess. `money-
 * format` script tags on the page carry the real `{{ shop.money_format
 * | json }}` string; formatMoney() implements Shopify's own public,
 * documented `{{amount}}` token substitution against it — the same
 * publicly documented technique Shopify's own docs describe, not
 * proprietary theme code.
 */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    function defaultTo(value, fallback) {
      return value == null || value !== value ? fallback : value;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '{{amount}}';
    switch (fmt.match(placeholder)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  function getMoneyFormat(root) {
    var tag = root.querySelector('[data-money-format]');
    if (!tag) return '{{amount}}';
    try {
      return JSON.parse(tag.textContent) || '{{amount}}';
    } catch (e) {
      return '{{amount}}';
    }
  }

  function parseYarnColor(value) {
    var color = (value || '').trim();
    var match;
    if ((match = color.match(/^#([0-9a-f]{3})$/i))) {
      return {
        r: parseInt(match[1][0] + match[1][0], 16),
        g: parseInt(match[1][1] + match[1][1], 16),
        b: parseInt(match[1][2] + match[1][2], 16)
      };
    }
    if ((match = color.match(/^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/i))) {
      return {
        r: parseInt(match[1].slice(0, 2), 16),
        g: parseInt(match[1].slice(2, 4), 16),
        b: parseInt(match[1].slice(4, 6), 16)
      };
    }
    if ((match = color.match(/^rgba?\(\s*(\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)/i))) {
      return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
    }
    return null;
  }

  function yarnRgbToHsl(rgb) {
    var r = rgb.r / 255;
    var g = rgb.g / 255;
    var b = rgb.b / 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var h = 0;
    var s = 0;
    var l = (max + min) / 2;
    var delta = max - min;
    if (delta) {
      s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
      if (max === r) h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / delta + 2) / 6;
      else h = ((r - g) / delta + 4) / 6;
    }
    return { h: h * 360, s: s, l: l };
  }

  function yarnFrameColor(value) {
    var rgb = parseYarnColor(value);
    if (!rgb) return '';
    var hsl = yarnRgbToHsl(rgb);
    var displayLightness = hsl.l > 0.82 ? 0.68 : hsl.l;
    return 'hsl(' + Math.round(hsl.h) + ', ' + Math.round(hsl.s * 100) + '%, ' + Math.round(displayLightness * 100) + '%)';
  }

  function updateYarnFrameColor(root, opt) {
    if (!root.classList.contains('mvc-pdp-yarn')) return;
    var displayColor = yarnFrameColor(opt.getAttribute('data-swatch-color'));
    if (displayColor) root.style.setProperty('--selected-yarn-color', displayColor);
    else root.style.removeProperty('--selected-yarn-color');
  }

  function isYarnRoot(root) {
    return Boolean(root && root.classList.contains('mvc-pdp-yarn'));
  }

  function isYarnRealStockOption(opt) {
    if (!opt || opt.getAttribute('data-shopify-available') !== 'true') return false;
    var inventoryManagement = opt.getAttribute('data-inventory-management') || '';
    if (!inventoryManagement) return true;
    var inventoryQuantity = parseInt(opt.getAttribute('data-inventory-quantity'), 10);
    return !isNaN(inventoryQuantity) && inventoryQuantity > 0;
  }

  function optionCanPurchase(root, opt) {
    if (!opt) return false;
    return isYarnRoot(root)
      ? isYarnRealStockOption(opt)
      : opt.getAttribute('data-available') === 'true';
  }

  function initVariantPicker(root) {
    var select = root.querySelector('[data-variant-select]');
    if (!select || select.dataset.mvcBound) return;
    select.dataset.mvcBound = 'true';

    var state = {};
    var initialOpt = select.options[select.selectedIndex];
    if (initialOpt) {
      [1, 2, 3].forEach(function (pos) {
        var v = initialOpt.getAttribute('data-option' + pos);
        if (v) state[pos] = v;
      });
    }

    function findMatchingOption(candidateState) {
      for (var i = 0; i < select.options.length; i++) {
        var o = select.options[i];
        var matches = true;
        for (var pos in candidateState) {
          if (o.getAttribute('data-option' + pos) !== candidateState[pos]) {
            matches = false;
            break;
          }
        }
        if (matches) return o;
      }
      return null;
    }

    // Candidate-then-commit: a click never changes visible/selected state
    // until a REAL variant matching the full resulting combination is
    // confirmed to exist. An existing-but-sold-out variant is a valid
    // commit (applyVariant shows the sold-out state); a combination with
    // no matching variant at all is rejected outright — the previous
    // real state (visual + submitted `id`) is left untouched.
    function selectValue(position, value, btn) {
      var candidate = {};
      for (var pos in state) candidate[pos] = state[pos];
      candidate[position] = value;

      var opt = findMatchingOption(candidate);
      if (!opt) return;

      // A Yarn color click must preserve every other visible option. If
      // that exact combination is not real-stock, reject the click instead
      // of silently substituting the card's representative variant (which
      // could change an unrelated size/weight option). Non-color option
      // clicks may explicitly represent an unavailable combination, but
      // applyVariant() keeps the purchase controls disabled until the
      // customer chooses a complete real-stock combination.
      if (isYarnRoot(root) && btn) {
        var colorPosition = root.getAttribute('data-color-option-position') || '';
        if (position === colorPosition && !optionCanPurchase(root, opt)) return;
      }

      if (isYarnRoot(root)) {
        state = {};
        [1, 2, 3].forEach(function (optionPosition) {
          var optionValue = opt.getAttribute('data-option' + optionPosition);
          if (optionValue) state[optionPosition] = optionValue;
        });
        root.querySelectorAll('[data-option-position]').forEach(function (b) {
          var buttonPosition = b.getAttribute('data-option-position');
          var on = state[buttonPosition] === b.getAttribute('data-option-value');
          b.classList.toggle('on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      } else {
        state = candidate;
        root.querySelectorAll('[data-option-position="' + position + '"]').forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      select.value = opt.value;
      applyVariant(opt, root);
    }

    if (isYarnRoot(root)) {
      root.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-option-position]');
        if (!btn || !root.contains(btn) || btn.disabled) return;
        selectValue(btn.getAttribute('data-option-position'), btn.getAttribute('data-option-value'), btn);
      });
    } else {
      root.querySelectorAll('[data-option-position]').forEach(function (btn) {
        if (btn.dataset.mvcBound || btn.disabled) return;
        btn.dataset.mvcBound = 'true';
        btn.addEventListener('click', function () {
          selectValue(btn.getAttribute('data-option-position'), btn.getAttribute('data-option-value'), btn);
        });
      });
    }

    if (initialOpt) applyVariant(initialOpt, root);
  }

  function applyVariant(opt, root) {
    var available = optionCanPurchase(root, opt);
    var priceFormatted = opt.getAttribute('data-price-formatted') || '';
    var compareAtFormatted = opt.getAttribute('data-compare-at-formatted') || '';
    var priceCents = parseInt(opt.getAttribute('data-price-cents'), 10);
    var compareAtCents = parseInt(opt.getAttribute('data-compare-at-cents'), 10);
    var image = opt.getAttribute('data-featured-image') || '';
    var defaultImage = root.getAttribute('data-product-default-image') || '';
    var invManagement = opt.getAttribute('data-inventory-management') || '';
    var invQty = parseInt(opt.getAttribute('data-inventory-quantity'), 10);
    var lowThreshold = parseInt(root.getAttribute('data-low-stock-threshold') || '6', 10);
    var onSale = available && !isNaN(compareAtCents) && !isNaN(priceCents) && compareAtCents > priceCents;

    updateYarnFrameColor(root, opt);

    root.querySelectorAll('[data-price-display]').forEach(function (el) {
      el.textContent = priceFormatted;
    });
    root.querySelectorAll('[data-compare-price-display]').forEach(function (el) {
      el.textContent = onSale ? compareAtFormatted : '';
      el.hidden = !onSale;
    });
    root.querySelectorAll('[data-save-flag]').forEach(function (el) {
      el.hidden = !onSale;
      if (onSale) {
        var pct = Math.round(((compareAtCents - priceCents) / compareAtCents) * 100);
        el.textContent = (el.getAttribute('data-save-flag-prefix') || '') + pct + '%';
      }
    });

    // Variant image consistency: a real variant image always wins. A
    // variant with no image of its own falls back to the product's own
    // general image for the main gallery (never a stale sibling
    // variant's image), and to a neutral empty state for the small
    // swatch/badge indicator (which exists specifically to represent
    // THIS variant, so it must never keep showing another variant's
    // picture).
    var galleryImage = image || defaultImage;
    if (galleryImage) {
      root.querySelectorAll('[data-gallery-main-image]').forEach(function (img) {
        img.src = galleryImage;
      });
    }
    root.querySelectorAll('[data-swatch-badge-image]').forEach(function (el) {
      el.style.backgroundImage = image ? "url('" + image + "')" : '';
    });
    root.querySelectorAll('[data-variant-label]').forEach(function (el) {
      el.textContent = opt.getAttribute('data-shade-title') || opt.getAttribute('data-title') || opt.textContent;
    });

    var stockEls = root.querySelectorAll('[data-stock-text]');
    stockEls.forEach(function (el) {
      var cls = 'stock';
      var text = '';
      if (!available) {
        cls += ' none';
        text = el.getAttribute('data-label-out') || '';
      } else if (invManagement === 'shopify' && !isNaN(invQty) && invQty > 0 && invQty <= lowThreshold) {
        cls += ' low';
        text = (el.getAttribute('data-label-low') || '').replace('[qty]', invQty);
      } else {
        text = el.getAttribute('data-label-in') || '';
      }
      el.className = cls;
      var textNode = el.querySelector('[data-stock-text-value]');
      if (textNode) textNode.textContent = text;
    });

    updateAddToCart(root, available);
    updateQtyTotals(root);
    updateBundleSummary(root);
    updateQuickBuy(root, opt, available);
    updatePricePer100m(root, opt);
  }

  // Yarn's "…ל-100 מטר" price line: product.metafields.custom.
  // yarn_length_meters is a real, constant-per-product PRODUCT
  // metafield (the length of one skein never changes by variant), but
  // price genuinely does — so this recomputes on every real variant
  // change from that variant's own real data-price-cents, entirely in
  // integer currency subunits, and reuses the shared getMoneyFormat()/
  // formatMoney() already used everywhere else on this page. A no-op
  // on Digital/Physical (no [data-price-per-100m-wrap] element there)
  // and a safe no-op here too whenever no valid length metafield was
  // rendered at all (data-yarn-length-meters absent/zero) — never a
  // divide-by-zero, never a placeholder number.
  function updatePricePer100m(root, opt) {
    var wrap = root.querySelector('[data-price-per-100m-wrap]');
    if (!wrap || !opt) return;
    var lengthMeters = parseFloat(root.getAttribute('data-yarn-length-meters'));
    var priceCents = parseInt(opt.getAttribute('data-price-cents'), 10);
    if (!lengthMeters || lengthMeters <= 0 || isNaN(priceCents)) {
      wrap.hidden = true;
      return;
    }
    var per100mCents = Math.round((priceCents * 100) / lengthMeters);
    var target = wrap.querySelector('[data-price-per-100m]');
    if (target) target.textContent = formatMoney(per100mCents, getMoneyFormat(root));
    wrap.hidden = false;
  }

  // Digital's "קנייה מהירה": a real Shopify cart permalink
  // (`{{ routes.root_url }}cart/{variant_id}:1`) — a plain navigable
  // link, not a form submission, so it works whether or not the
  // product has real options. Kept in sync with the currently selected
  // variant on every applyVariant() call; an unavailable variant drops
  // its href entirely (an <a> with no href is natively non-interactive
  // — not a fake disabled state).
  function updateQuickBuy(root, opt, available) {
    var base = root.getAttribute('data-cart-permalink-base');
    if (!base) return;
    root.querySelectorAll('[data-quick-buy]').forEach(function (el) {
      if (available && opt) {
        el.setAttribute('href', base + opt.value + ':1');
        el.removeAttribute('aria-disabled');
      } else {
        el.removeAttribute('href');
        el.setAttribute('aria-disabled', 'true');
      }
    });
  }

  function updateAddToCart(root, available) {
    root.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      btn.disabled = !available;
      var labelEl = btn.querySelector('[data-add-to-cart-label]');
      var priceEl = btn.querySelector('[data-add-to-cart-price]');
      if (available) {
        if (labelEl) labelEl.textContent = btn.getAttribute('data-label-available') || '';
        if (priceEl) priceEl.hidden = false;
      } else {
        if (labelEl) labelEl.textContent = btn.getAttribute('data-label-unavailable') || '';
        if (priceEl) priceEl.hidden = true;
      }
    });
  }

  function updateQtyTotals(root) {
    var qtyInput = root.querySelector('[data-qty-input]');
    var select = root.querySelector('[data-variant-select]');
    if (!select) return;
    var opt = select.options[select.selectedIndex];
    if (!opt) return;
    var cents = parseInt(opt.getAttribute('data-price-cents'), 10);
    if (isNaN(cents)) return;
    var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    var format = getMoneyFormat(root);
    var totalFormatted = formatMoney(cents * qty, format);

    root.querySelectorAll('[data-add-to-cart-price]').forEach(function (el) {
      el.textContent = totalFormatted;
    });
    root.querySelectorAll('[data-mbar-price]').forEach(function (el) {
      el.textContent = totalFormatted;
    });
  }

  function initQty(root) {
    var input = root.querySelector('[data-qty-input]');
    if (!input || input.dataset.mvcBound) return;
    input.dataset.mvcBound = 'true';
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var max = parseInt(input.getAttribute('max'), 10) || 99;

    function clamp() {
      var v = Math.max(min, Math.min(max, parseInt(input.value, 10) || min));
      input.value = v;
      updateQtyTotals(root);
    }

    var minus = root.querySelector('[data-qty-minus]');
    var plus = root.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', function () { input.value = (parseInt(input.value, 10) || min) - 1; clamp(); });
    if (plus) plus.addEventListener('click', function () { input.value = (parseInt(input.value, 10) || min) + 1; clamp(); });
    input.addEventListener('input', clamp);
  }

  /**
   * DIGITAL "what you need" BUNDLE BUILDER.
   *
   * A base product plus togglable real material rows. Selecting at
   * least the configured number of materials unlocks the configured 5%-off preview on the
   * running subtotal (base + selected materials):
   * `data-bundle-discount-min` and `data-bundle-discount-percent`
   * mirror the real SMART Functions Creator rule configured in Shopify.
   * The base pattern carries the private `_mvc_project_recipe` list.
   * The Ajax Cart compares it with every cart line after each change
   * and applies `_mvc_project_bundle=true` only to the pattern and
   * compatible materials, even when a material is added later elsewhere.
   * This code only previews the same percentage and never modifies a
   * Shopify price directly.
   *
   * Every computation uses integer currency subunits (`data-price-
   * cents`, matching this project's other established money-subunit
   * fix) — never floating-point major-unit arithmetic.
   */
  function getBundleRowVariant(row) {
    var select = row.querySelector('[data-bundle-variant-select]');
    if (select) {
      var opt = select.options[select.selectedIndex];
      if (!opt) return null;
      return {
        id: opt.value,
        cents: parseInt(opt.getAttribute('data-price-cents'), 10),
        formatted: opt.getAttribute('data-price-formatted') || '',
        available: opt.getAttribute('data-available') === 'true',
        image: opt.getAttribute('data-image') || ''
      };
    }
    var priceEl = row.querySelector('[data-bundle-item-price]');
    if (!priceEl) return null;
    return {
      id: priceEl.getAttribute('data-default-variant-id'),
      cents: parseInt(priceEl.getAttribute('data-default-price-cents'), 10),
      formatted: null,
      available: priceEl.getAttribute('data-default-available') === 'true',
      image: ''
    };
  }

  function getBundleRowQuantity(row) {
    var quantity = parseInt(row.getAttribute('data-quantity'), 10);
    return !isNaN(quantity) && quantity > 0 ? quantity : 1;
  }

  function updateBundleSummary(root) {
    var bundle = root.querySelector('[data-bundle]');
    if (!bundle) return;

    var baseSelect = root.querySelector('[data-variant-select]');
    var baseOpt = baseSelect ? baseSelect.options[baseSelect.selectedIndex] : null;
    var baseCents = baseOpt ? parseInt(baseOpt.getAttribute('data-price-cents'), 10) : NaN;
    var baseAvailable = baseOpt ? baseOpt.getAttribute('data-available') === 'true' : false;
    var baseTitle = bundle.getAttribute('data-bundle-base-title') || '';

    var lines = [];
    if (baseOpt && baseAvailable && !isNaN(baseCents)) {
      lines.push({ title: baseTitle, cents: baseCents, unitCents: baseCents, quantity: 1 });
    }

    var selectedCount = 0;
    var format = getMoneyFormat(root);
    bundle.querySelectorAll('[data-bundle-item]').forEach(function (row) {
      var cb = row.querySelector('[data-bundle-checkbox]');
      var variant = getBundleRowVariant(row);
      var quantity = getBundleRowQuantity(row);
      var priceEl = row.querySelector('[data-bundle-item-price]');
      if (variant && priceEl) {
        var priceTarget = priceEl.querySelector('bdi') || priceEl;
        priceTarget.textContent = formatMoney(variant.cents * quantity, format);
      }
      if (variant && variant.image) {
        var img = row.querySelector('img');
        if (img) img.src = variant.image;
      }
      if (cb && row.hasAttribute('data-project-material')) {
        cb.disabled = !(variant && variant.available);
      }
      row.classList.toggle('is-unavailable', !(variant && variant.available));
      var alternativePicker = row.querySelector('[data-bundle-variant-select]');
      var alternativeNote = row.querySelector('.mi-alt-note');
      if (alternativeNote) alternativeNote.hidden = !!(variant && variant.available);
      var stockStatus = row.querySelector('[data-bundle-stock-status]');
      if (stockStatus) {
        stockStatus.hidden = !!(variant && variant.available) || !!alternativePicker;
      }
      var isOn = !!(cb && cb.checked && !cb.disabled && variant && variant.available);
      row.classList.toggle('on', isOn);
      if (isOn) {
        var lineTitle = row.getAttribute('data-title') || '';
        if (quantity > 1) lineTitle += ' × ' + quantity;
        lines.push({ title: lineTitle, cents: variant.cents * quantity, unitCents: variant.cents, quantity: quantity });
        selectedCount++;
      }
    });

    var subtotalCents = lines.reduce(function (a, l) { return a + (isNaN(l.cents) ? 0 : l.cents); }, 0);
    var discountEnabled = bundle.getAttribute('data-bundle-discount-enabled') === 'true';
    var minMaterials = parseInt(bundle.getAttribute('data-bundle-discount-min'), 10) || 2;
    var percent = parseFloat(bundle.getAttribute('data-bundle-discount-percent')) || 0;
    var eligible = discountEnabled && selectedCount >= minMaterials;
    // Match the observed Shopify discount: round down per unit, then multiply by quantity.
    var discountCents = eligible ? lines.reduce(function (sum, line) {
      return sum + Math.floor(line.unitCents * percent / 100) * line.quantity;
    }, 0) : 0;
    var totalCents = subtotalCents - discountCents;

    var linesEl = bundle.querySelector('[data-bundle-lines]');
    if (linesEl) {
      linesEl.innerHTML = lines.map(function (l) {
        return '<div class="msrow"><span></span><span class="num"></span></div>';
      }).join('');
      var rowEls = linesEl.querySelectorAll('.msrow');
      lines.forEach(function (l, i) {
        var spans = rowEls[i].querySelectorAll('span');
        spans[0].textContent = l.title;
        spans[1].textContent = formatMoney(l.cents, format);
      });
    }

    var discountRow = bundle.querySelector('[data-bundle-discount-row]');
    if (discountRow) {
      discountRow.hidden = !eligible;
      var amountEl = bundle.querySelector('[data-bundle-discount-amount]');
      if (amountEl) amountEl.textContent = '-' + formatMoney(discountCents, format);
    }

    var totalEl = bundle.querySelector('[data-bundle-total]');
    if (totalEl) totalEl.textContent = formatMoney(totalCents, format);

    var addAllBtn = bundle.querySelector('[data-bundle-add-all]');
    if (addAllBtn && !addAllBtn.dataset.mvcLoading) {
      addAllBtn.disabled = !baseAvailable;
    }
  }

  function projectRecipeIds(bundle) {
    var ids = (bundle.getAttribute('data-project-recipe') || '').split(',');
    bundle.querySelectorAll('[data-bundle-item][data-project-variant-id]').forEach(function (row) {
      ids.push(row.getAttribute('data-project-variant-id'));
    });
    return ids
      .map(function (id) { return String(id || '').trim(); })
      .filter(function (id, index, all) {
        return /^\d+$/.test(id) && all.indexOf(id) === index;
      });
  }

  function ensureProjectRecipeInput(root, form) {
    var bundle = root.querySelector('[data-bundle]');
    if (!bundle || !form) return null;
    var recipe = projectRecipeIds(bundle).join(',');
    if (!recipe) return null;

    var input = form.querySelector('input[name="properties[_mvc_project_recipe]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_mvc_project_recipe]';
      form.appendChild(input);
    }
    input.value = recipe;
    return input;
  }

  function collectBundleItems(root) {
    var items = [];
    var selectedVariantIds = [];
    var baseVariantId = null;

    function addItem(id, quantity) {
      var numericId = parseInt(id, 10);
      if (!numericId) return;
      var existing = items.find(function (item) { return item.id === numericId; });
      if (existing) existing.quantity += quantity;
      else items.push({ id: numericId, quantity: quantity });
    }

    var baseSelect = root.querySelector('[data-variant-select]');
    var baseOpt = baseSelect ? baseSelect.options[baseSelect.selectedIndex] : null;
    if (baseOpt && baseOpt.getAttribute('data-available') === 'true') {
      baseVariantId = parseInt(baseOpt.value, 10);
      addItem(baseOpt.value, 1);
    }

    var bundle = root.querySelector('[data-bundle]');
    if (!bundle) return items;
    bundle.querySelectorAll('[data-bundle-item]').forEach(function (row) {
      var cb = row.querySelector('[data-bundle-checkbox]');
      if (!cb || !cb.checked || cb.disabled) return;
      var variant = getBundleRowVariant(row);
      if (variant && variant.available && variant.id) {
        addItem(variant.id, getBundleRowQuantity(row));
        selectedVariantIds.push(String(variant.id));
      }
    });

    // Keep the full metafield recipe on the pattern line. If the customer
    // selected an in-stock substitute color in the bundle builder, include
    // that exact selected variant too. Cart reconciliation decides which
    // lines qualify; material lines never assign eligibility to themselves.
    var recipe = projectRecipeIds(bundle)
      .concat(selectedVariantIds)
      .filter(function (id, index, all) { return all.indexOf(id) === index; })
      .join(',');
    var baseItem = items.find(function (item) { return item.id === baseVariantId; });
    if (baseItem && recipe) {
      baseItem.properties = baseItem.properties || {};
      baseItem.properties._mvc_project_recipe = recipe;
    }
    return items;
  }

  function setProductFormBusy(root, form, busy) {
    form.setAttribute('aria-busy', busy ? 'true' : 'false');
    root.querySelectorAll('[data-add-to-cart]').forEach(function (button) {
      if (busy) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
    if (!busy) {
      var select = root.querySelector('[data-variant-select]');
      var option = select && select.options[select.selectedIndex];
      updateAddToCart(root, optionCanPurchase(root, option));
    }
  }

  function initAjaxProductForms(root) {
    root.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      if (form.dataset.mvcCartBound) return;
      form.dataset.mvcCartBound = 'true';
      form.addEventListener('submit', function (event) {
        if (isYarnRoot(root)) {
          var variantSelect = form.querySelector('[data-variant-select]');
          var selectedOption = variantSelect && variantSelect.options[variantSelect.selectedIndex];
          if (!optionCanPurchase(root, selectedOption)) {
            event.preventDefault();
            updateAddToCart(root, false);
            return;
          }
        }
        ensureProjectRecipeInput(root, form);
        if (event.defaultPrevented || !window.MvcCart || !window.fetch || !window.FormData) return;
        event.preventDefault();
        if (form.getAttribute('aria-busy') === 'true') return;

        setProductFormBusy(root, form, true);
        window.MvcCart.addForm(form, {
          source: null,
          open: true,
          singleCopy: root.hasAttribute('data-single-copy-pattern'),
          successMessage: 'המוצר נוסף לעגלה.'
        }).then(function () {
          setProductFormBusy(root, form, false);
        }, function () {
          setProductFormBusy(root, form, false);
        });
      });
    });
  }

  function initProjectQuickBuy(root) {
    var link = root.querySelector('[data-quick-buy]');
    var form = root.querySelector('form[action*="/cart/add"]');
    var recipeInput = form && ensureProjectRecipeInput(root, form);
    if (!link || !form || !recipeInput || link.dataset.mvcProjectBound) return;
    link.dataset.mvcProjectBound = 'true';

    link.addEventListener('click', function (event) {
      if (!window.MvcCart || link.getAttribute('aria-disabled') === 'true') return;
      var select = form.querySelector('[data-variant-select]');
      var option = select && select.options[select.selectedIndex];
      if (!option || option.getAttribute('data-available') !== 'true') return;

      event.preventDefault();
      link.setAttribute('aria-busy', 'true');
      var destination = root.getAttribute('data-cart-permalink-base') || '/cart/';
      window.MvcCart.addItems([{
        id: parseInt(option.value, 10),
        quantity: 1,
        properties: { _mvc_project_recipe: recipeInput.value }
      }], {
        source: null,
        open: false,
        singleCopyVariantId: root.hasAttribute('data-single-copy-pattern') ? option.value : null,
        successMessage: 'המוצר נוסף לעגלה.'
      }).then(function () {
        window.location.href = destination;
      }, function () {
        link.removeAttribute('aria-busy');
      });
    });
  }

  function initBundle(root) {
    var bundle = root.querySelector('[data-bundle]');
    if (!bundle || bundle.dataset.mvcBound) return;
    bundle.dataset.mvcBound = 'true';

    bundle.querySelectorAll('[data-bundle-checkbox]').forEach(function (cb) {
      cb.addEventListener('change', function () { updateBundleSummary(root); });
    });
    bundle.querySelectorAll('[data-bundle-variant-select]').forEach(function (select) {
      select.addEventListener('change', function () { updateBundleSummary(root); });
    });

    var addAllBtn = bundle.querySelector('[data-bundle-add-all]');
    var errorEl = bundle.querySelector('[data-bundle-error]');
    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        if (addAllBtn.dataset.mvcLoading) return;
        var items = collectBundleItems(root);
        if (!items.length) return;

        var addUrl = bundle.getAttribute('data-cart-add-url');
        var cartUrl = bundle.getAttribute('data-cart-url');
        var originalLabel = bundle.getAttribute('data-bundle-add-all-label') || addAllBtn.textContent;
        var loadingLabel = bundle.getAttribute('data-bundle-loading-label') || originalLabel;

        addAllBtn.dataset.mvcLoading = 'true';
        addAllBtn.disabled = true;
        addAllBtn.textContent = loadingLabel;
        if (errorEl) errorEl.hidden = true;

        if (window.MvcCart) {
          window.MvcCart.addItems(items, {
            source: null,
            open: true,
            singleCopyVariantId: root.hasAttribute('data-single-copy-pattern') ? items[0].id : null,
            successMessage: 'כל פריטי החבילה נוספו לעגלה.'
          }).then(function () {
            delete addAllBtn.dataset.mvcLoading;
            addAllBtn.textContent = originalLabel;
            updateBundleSummary(root);
          }, function () {
            delete addAllBtn.dataset.mvcLoading;
            addAllBtn.disabled = false;
            addAllBtn.textContent = originalLabel;
            if (errorEl) {
              errorEl.textContent = bundle.getAttribute('data-bundle-error-label') || '';
              errorEl.hidden = false;
            }
          });
          return;
        }

        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            if (!res.ok) {
              return res.json().then(function (data) {
                throw new Error((data && (data.description || data.message)) || 'cart_add_failed');
              });
            }
            return res.json();
          })
          .then(function () {
            window.location.href = cartUrl;
          })
          .catch(function () {
            delete addAllBtn.dataset.mvcLoading;
            addAllBtn.disabled = false;
            addAllBtn.textContent = originalLabel;
            if (errorEl) {
              errorEl.textContent = bundle.getAttribute('data-bundle-error-label') || '';
              errorEl.hidden = false;
            }
          });
      });
    }

    updateBundleSummary(root);
  }

  function initAccordion(root) {
    root.querySelectorAll('[data-accordion-toggle]').forEach(function (btn) {
      if (btn.dataset.mvcBound) return;
      btn.dataset.mvcBound = 'true';
      btn.addEventListener('click', function () {
        var item = btn.closest('.acci');
        if (item) item.classList.toggle('open');
      });
    });
  }

  function initThumbs(root) {
    root.querySelectorAll('[data-thumb]').forEach(function (thumb) {
      if (thumb.dataset.mvcBound) return;
      thumb.dataset.mvcBound = 'true';
      thumb.addEventListener('click', function () {
        root.querySelectorAll('[data-thumb]').forEach(function (t) { t.classList.remove('on'); });
        thumb.classList.add('on');
        var fullSrc = thumb.getAttribute('data-thumb-full');
        if (fullSrc) {
          root.querySelectorAll('[data-gallery-main-image]').forEach(function (img) {
            img.src = fullSrc;
          });
        }
      });
    });
  }

  // Accessory's size-guide drawer: a real merchant-authored richtext
  // note (no fabricated compatibility table), shown in the reference's
  // own bottom-sheet drawer geometry. Only rendered at all when a
  // merchant has filled in real content (see main-product-physical.
  // liquid), so this binds defensively and no-ops if absent.
  function initGuide(root) {
    var trigger = root.querySelector('[data-guide-trigger]');
    var guide = root.querySelector('[data-guide]');
    if (!trigger || !guide || guide.dataset.mvcBound) return;
    guide.dataset.mvcBound = 'true';
    var closeBtn = guide.querySelector('[data-guide-close]');

    function open() { guide.classList.add('open'); }
    function close() { guide.classList.remove('open'); }

    trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    guide.addEventListener('click', function (e) {
      if (e.target === guide) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  var yarnFamilyDefinitions = [
    { key: 'light-neutral', label: 'ניטרליים', order: 0, aliases: ['ניטרל', 'לבן', 'שמנת', 'cream', 'white', 'neutral'] },
    { key: 'beige-brown', label: 'בז׳ וחום', order: 1, aliases: ['בז', 'חום', 'beige', 'brown', 'camel'] },
    { key: 'pink', label: 'ורוד', order: 2, aliases: ['ורוד', 'pink'] },
    { key: 'red', label: 'אדום', order: 3, aliases: ['אדום', 'red'] },
    { key: 'orange-yellow', label: 'כתום וצהוב', order: 4, aliases: ['כתום', 'צהוב', 'orange', 'yellow'] },
    { key: 'green', label: 'ירוק', order: 5, aliases: ['ירוק', 'green'] },
    { key: 'turquoise', label: 'טורקיז', order: 6, aliases: ['טורקיז', 'ציאן', 'cyan', 'turquoise', 'aqua'] },
    { key: 'blue', label: 'כחול', order: 7, aliases: ['כחול', 'תכלת', 'blue'] },
    { key: 'purple', label: 'סגול', order: 8, aliases: ['סגול', 'לילך', 'purple', 'violet', 'lilac'] },
    { key: 'gray-black', label: 'אפור ושחור', order: 9, aliases: ['אפור', 'שחור', 'gray', 'grey', 'black'] },
    { key: 'multicolor', label: 'רב־גוני', order: 10, aliases: ['רב־גוני', 'רב-גוני', 'רב גוני', 'multicolor', 'multi-color', 'variegated'] }
  ];

  function canonicalYarnFamily(rawFamily) {
    var value = (rawFamily || '').trim().toLowerCase();
    if (!value) return null;
    for (var i = 0; i < yarnFamilyDefinitions.length; i++) {
      var definition = yarnFamilyDefinitions[i];
      for (var j = 0; j < definition.aliases.length; j++) {
        if (value.indexOf(definition.aliases[j]) !== -1) return definition;
      }
    }
    return { key: 'merchant:' + value, label: rawFamily.trim(), order: 11 };
  }

  function derivedYarnFamily(hsl) {
    if (hsl.s < 0.12) {
      return hsl.l > 0.86 ? yarnFamilyDefinitions[0] : yarnFamilyDefinitions[9];
    }
    if (hsl.l > 0.82 && hsl.s < 0.35) return yarnFamilyDefinitions[0];
    if (hsl.h >= 18 && hsl.h < 55 && (hsl.s < 0.55 || hsl.l < 0.62)) return yarnFamilyDefinitions[1];
    if (hsl.h >= 315 && hsl.h < 350 && hsl.l >= 0.42) return yarnFamilyDefinitions[2];
    if (hsl.h >= 350 || hsl.h < 15) return yarnFamilyDefinitions[3];
    if (hsl.h < 70) return yarnFamilyDefinitions[4];
    if (hsl.h < 165) return yarnFamilyDefinitions[5];
    if (hsl.h < 195) return yarnFamilyDefinitions[6];
    if (hsl.h < 255) return yarnFamilyDefinitions[7];
    if (hsl.h < 315) return yarnFamilyDefinitions[8];
    return yarnFamilyDefinitions[2];
  }

  function yarnColorSortOrder(button) {
    var raw = button.getAttribute('data-color-sort-order');
    if (raw == null || String(raw).trim() === '') return null;
    var parsed = parseInt(raw, 10);
    return isNaN(parsed) ? null : parsed;
  }

  function yarnMatchingRealStockOption(root, button) {
    var select = root.querySelector('[data-variant-select]');
    if (!select || !button) return null;
    var selected = select.options[select.selectedIndex];
    var candidate = {};
    if (selected) {
      [1, 2, 3].forEach(function (position) {
        var value = selected.getAttribute('data-option' + position);
        if (value) candidate[position] = value;
      });
    }
    candidate[button.getAttribute('data-option-position')] = button.getAttribute('data-option-value');
    for (var i = 0; i < select.options.length; i++) {
      var option = select.options[i];
      var matches = true;
      for (var position in candidate) {
        if (option.getAttribute('data-option' + position) !== candidate[position]) {
          matches = false;
          break;
        }
      }
      if (matches && optionCanPurchase(root, option)) return option;
    }
    return null;
  }

  function applyYarnFamilyFilter(root, family, selectReplacement) {
    var picker = root.querySelector('.picker');
    if (!picker) return false;
    var shadeButtons = Array.prototype.slice.call(picker.querySelectorAll('.sh, .shade-pills .opt-pill'));
    shadeButtons.forEach(function (button) {
      var buttonFamily = button.getAttribute('data-effective-family') || '';
      button.hidden = !(!family || buttonFamily === family);
    });

    if (!selectReplacement) return true;
    var selected = picker.querySelector('.sh.on, .shade-pills .opt-pill.on');
    if (selected && !selected.hidden && selected.getAttribute('data-available') === 'true') return true;
    var firstVisible = shadeButtons.find(function (button) {
      return !button.hidden && button.getAttribute('data-available') === 'true' && yarnMatchingRealStockOption(root, button);
    });
    if (firstVisible) {
      firstVisible.click();
      return true;
    }

    // No shade in this family matches the customer's currently selected
    // secondary options. Restore the unfiltered view and current selection;
    // never choose an arbitrary secondary option just to satisfy a filter.
    shadeButtons.forEach(function (button) { button.hidden = false; });
    var filterRow = root.querySelector('[data-shade-family-filters]');
    if (filterRow) {
      filterRow.setAttribute('data-active-family', '');
      filterRow.querySelectorAll('[data-family-filter]').forEach(function (chip) {
        chip.classList.toggle('on', (chip.getAttribute('data-family-filter') || '') === '');
      });
    }
    return false;
  }

  // Display-only sorting. custom.color_sort_order is authoritative;
  // missing/invalid values sort last and retain their Shopify-relative
  // order. The real variant select, IDs, images, inventory, and product
  // data are never reordered or rewritten.
  function initYarnShadePresentation(root) {
    if (!root.classList.contains('mvc-pdp-yarn')) return;
    var picker = root.querySelector('.picker');
    var shadeGrid = picker && (picker.querySelector('.shades') || picker.querySelector('.shade-pills'));
    var filterRow = root.querySelector('[data-shade-family-filters]');
    if (!shadeGrid || !filterRow) return;
    if (picker.mvcYarnPickerObserver) picker.mvcYarnPickerObserver.disconnect();

    var shades = Array.prototype.slice.call(shadeGrid.querySelectorAll('.sh, .opt-pill')).filter(function (button) {
      return button.getAttribute('data-available') === 'true';
    });
    var familyMap = {};
    var missingColors = 0;
    var sortable = shades.map(function (button, index) {
      var originalOrder = parseInt(button.getAttribute('data-original-order'), 10);
      if (isNaN(originalOrder)) originalOrder = index;
      var sortOrder = yarnColorSortOrder(button);
      var rgb = parseYarnColor(button.getAttribute('data-swatch-color'));
      var hsl = rgb ? yarnRgbToHsl(rgb) : null;
      var rawFamily = button.getAttribute('data-color-family') || '';
      var family = rawFamily ? canonicalYarnFamily(rawFamily) : (hsl ? derivedYarnFamily(hsl) : null);
      if (!hsl) missingColors++;
      if (family) {
        button.setAttribute('data-effective-family', family.key);
        if (!familyMap[family.key]) {
          familyMap[family.key] = { key: family.key, label: family.label, order: family.order, count: 0 };
        }
        familyMap[family.key].count++;
      } else {
        button.removeAttribute('data-effective-family');
      }
      return { button: button, original: originalOrder, sortOrder: sortOrder };
    });

    sortable.sort(function (a, b) {
      if (a.sortOrder == null && b.sortOrder != null) return 1;
      if (a.sortOrder != null && b.sortOrder == null) return -1;
      if (a.sortOrder != null && b.sortOrder != null && a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.original - b.original;
    });
    sortable.forEach(function (item) { shadeGrid.appendChild(item.button); });

    filterRow.querySelectorAll('[data-generated-family-filter]').forEach(function (chip) { chip.remove(); });
    var allChip = filterRow.querySelector('[data-family-filter=""]');
    if (allChip) {
      allChip.setAttribute('data-family-count', String(shades.length));
      allChip.setAttribute('aria-label', allChip.textContent.trim() + ', ' + shades.length + ' גוונים זמינים');
    }
    Object.keys(familyMap).map(function (key) { return familyMap[key]; }).sort(function (a, b) {
      if (a.order !== b.order) return a.order - b.order;
      return a.label.localeCompare(b.label, 'he');
    }).forEach(function (family) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'fchip';
      chip.setAttribute('data-family-filter', family.key);
      chip.setAttribute('data-generated-family-filter', 'true');
      chip.setAttribute('data-family-count', String(family.count));
      chip.setAttribute('aria-label', family.label + ', ' + family.count + ' גוונים זמינים');
      chip.textContent = family.label;
      filterRow.appendChild(chip);
    });
    filterRow.hidden = Object.keys(familyMap).length === 0;
    root.setAttribute('data-missing-swatch-colors', String(missingColors));

    var activeFamily = filterRow.getAttribute('data-active-family') || '';
    if (activeFamily && !familyMap[activeFamily]) activeFamily = '';
    filterRow.setAttribute('data-active-family', activeFamily);
    filterRow.querySelectorAll('[data-family-filter]').forEach(function (chip) {
      chip.classList.toggle('on', (chip.getAttribute('data-family-filter') || '') === activeFamily);
    });
    applyYarnFamilyFilter(root, activeFamily, activeFamily !== '');

    if (!picker.mvcYarnPickerObserver && typeof MutationObserver !== 'undefined') {
      picker.mvcYarnPickerObserver = new MutationObserver(function () {
        initYarnShadePresentation(root);
      });
    }
    if (picker.mvcYarnPickerObserver) {
      picker.mvcYarnPickerObserver.observe(picker, { childList: true, subtree: true });
    }
  }

  // One delegated listener survives generated-chip/DOM rebuilds. When a
  // family would hide the selected shade, the first available shade in
  // the already-sorted family is selected through the existing picker
  // click path, so a hidden/unavailable ID can never be submitted.
  function initShadeFamilyFilter(root) {
    var picker = root.querySelector('.picker');
    var filterRow = root.querySelector('[data-shade-family-filters]');
    if (!picker || !filterRow || filterRow.dataset.mvcBound) return;
    filterRow.dataset.mvcBound = 'true';

    filterRow.addEventListener('click', function (event) {
      var chip = event.target.closest('[data-family-filter]');
      if (!chip || !filterRow.contains(chip)) return;
      var family = chip.getAttribute('data-family-filter') || '';
      filterRow.setAttribute('data-active-family', family);
      filterRow.querySelectorAll('[data-family-filter]').forEach(function (candidate) {
        candidate.classList.toggle('on', candidate === chip);
      });
      applyYarnFamilyFilter(root, family, true);
    });
  }

  function initYarnMobilePurchaseBar(root) {
    if (!isYarnRoot(root) || root.dataset.mvcYarnMobileBarBound) return;
    var mainPurchaseRow = root.querySelector('[data-yarn-main-purchase-row]');
    var mobileBar = root.querySelector('[data-yarn-mobile-purchase-bar]');
    if (!mainPurchaseRow || !mobileBar || typeof IntersectionObserver === 'undefined') return;
    root.dataset.mvcYarnMobileBarBound = 'true';
    root.mvcYarnPurchaseObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target === mainPurchaseRow) {
          mobileBar.classList.toggle('is-main-purchase-visible', entry.isIntersecting);
        }
      });
    }, { threshold: 0.01 });
    root.mvcYarnPurchaseObserver.observe(mainPurchaseRow);
  }

  function init() {
    document.querySelectorAll('.mvc-pdp').forEach(function (root) {
      initVariantPicker(root);
      initYarnShadePresentation(root);
      initQty(root);
      initAccordion(root);
      initThumbs(root);
      initGuide(root);
      initAjaxProductForms(root);
      initProjectQuickBuy(root);
      initBundle(root);
      initShadeFamilyFilter(root);
      initYarnMobilePurchaseBar(root);
    });
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:block:select', init);
  document.addEventListener('shopify:block:deselect', init);
})();
